import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Course } from '../../../mocks/courses';
import { api } from '@/lib/api';
import { adaptCourseListItem, buildCategoryOptions, createCategoryNameResolver } from '@/lib/adapters';
import CoursePaymentModal from '../../course/components/CoursePaymentModal';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const FeaturedCourses = () => {
  const navigate = useNavigate();
  const { ref, isVisible } = useScrollAnimation<HTMLElement>({ threshold: 0.1 });
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalCourse, setModalCourse] = useState<Course | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const categories = await api.getCategories();
        buildCategoryOptions(categories);
        const categoryNameOf = createCategoryNameResolver(categories);
        const page = await api.searchCourses({ pageCurrent: 1, pageSize: 4 });
        setCourses(page.list.map((item) => adaptCourseListItem(item, categoryNameOf(item.categoryId))));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <section
      ref={ref}
      className="py-20 bg-[#F7F8FB]"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(32px)',
        transition: 'opacity 0.7s ease, transform 0.7s ease'
      }}
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-end justify-between mb-10">
          <div>
            <span className="text-xs font-semibold text-violet-500 tracking-widest uppercase mb-2 block">
              課程中心
            </span>
            <h2 className="text-3xl font-black text-[#0A1628] leading-tight">
              精選課程
              <br />
              <span className="text-slate-400 font-medium text-xl">直接接入後端課程資料</span>
            </h2>
          </div>
          <button
            onClick={() => navigate('/courses')}
            className="hidden sm:flex items-center gap-2 text-sm font-semibold text-[#0A1628] hover:text-violet-600 cursor-pointer transition-colors whitespace-nowrap group"
          >
            查看全部課程
          </button>
        </div>

        {loading ? (
          <div className="text-sm text-slate-400">課程資料載入中...</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
            {courses.map((course, index) => (
              <div
                key={course.id}
                className="bg-white rounded-2xl overflow-hidden border border-slate-100 hover:border-violet-200 hover:-translate-y-1 transition-all duration-300 group cursor-pointer flex flex-col"
                style={{
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
                  transition: `opacity 0.5s ease ${index * 80}ms, transform 0.5s ease ${index * 80}ms`
                }}
                onClick={() => navigate(`/course/${course.id}`)}
              >
                <div className={`bg-gradient-to-br ${course.gradient} px-5 pt-5 pb-4`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-xl">
                      <i className={`${course.icon} text-white text-lg`} />
                    </div>
                    <span className={`${course.tagColor} text-white text-[10px] font-bold px-2.5 py-1 rounded-full whitespace-nowrap`}>
                      {course.tag}
                    </span>
                  </div>
                  <h3 className="text-white font-bold text-sm leading-snug mb-1 line-clamp-2">{course.title}</h3>
                  <p className="text-white/60 text-[11px]">{course.level} · {course.duration}</p>
                </div>

                <div className="p-4 flex flex-col flex-1">
                  <div className="flex items-center gap-1.5 mb-3 text-xs text-slate-500">
                    <i className="ri-user-line text-violet-500" />
                    <span className="truncate">{course.instructor}</span>
                  </div>

                  <div className="flex items-center gap-3 text-[11px] text-slate-400 mb-4">
                    <span className="flex items-center gap-0.5">
                      <i className="ri-star-fill text-amber-400 text-xs" />
                      <span className="font-bold text-[#0A1628]">{course.rating}</span>
                    </span>
                    <span className="flex items-center gap-0.5">
                      <i className="ri-group-line" />
                      {course.students.toLocaleString()}
                    </span>
                  </div>

                  <div className="mt-auto pt-3 border-t border-slate-100">
                    <div className="flex items-baseline gap-1.5 mb-3">
                      <span className="text-lg font-black text-[#0A1628]">${course.price}</span>
                      <span className="text-xs text-slate-300 line-through">${course.originalPrice}</span>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={(event) => {
                          event.stopPropagation();
                          navigate(`/course/${course.id}`);
                        }}
                        className="py-2 border border-slate-200 text-slate-500 text-[11px] font-semibold rounded-lg cursor-pointer hover:border-violet-300 hover:text-violet-600 transition-all whitespace-nowrap"
                      >
                        了解詳情
                      </button>
                      <button
                        onClick={(event) => {
                          event.stopPropagation();
                          setModalCourse(course);
                        }}
                        className="py-2 bg-violet-600 text-white text-[11px] font-bold rounded-lg cursor-pointer hover:bg-violet-700 active:scale-95 transition-all whitespace-nowrap"
                      >
                        立即購買
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {modalCourse && (
          <CoursePaymentModal
            course={modalCourse}
            onClose={() => setModalCourse(null)}
            onSuccess={() => setModalCourse(null)}
          />
        )}
      </div>
    </section>
  );
};

export default FeaturedCourses;
